/**
 * The in-app placement diagnostic (Meliesa, Oct 29: "we'll build an in-app diagnostic so that we can take this
 * nationwide"). Placement is by what the student can do, never by grade:
 *   1. The times tables first — a sample from every table, timed internally (never shown). A student who isn't solid
 *      starts Tigard Times at the beginning, taught as is, whatever their grade.
 *   2. Then the lessons, starting near the student's grade: two problems from a lesson; both right moves up,
 *      a miss moves back (the jumps double), then it halves the gap until it finds the boundary.
 *   3. The starting point is the first lesson after the last one the student showed. A teacher can change it.
 * Pure logic here (tested); the screens are in ui/lessonView.ts.
 */
import { int } from "./core";
import { TEACHING } from "./levels";
import type { Item, Lesson } from "./types";
import { SKILLS } from "./skills";
import { mixedFactItem } from "./skills/level3-l9to13";
import { PROBE_SWAP, isReviewSkill } from "./plainProbes";

/** Solid on the facts: this many right of the sample, with the middle answer this fast (ms). */
export const FACTS_N = 12, FACTS_NEED = 11, FACTS_MEDIAN_MS = 5000;
/** The division sample that follows (Lesson 8's units are skipped only when this is solid too). */
export const DIV_N = 6, DIV_NEED = 5;
/** How far a passed probe jumps ahead. */
export const STRIDE = 2;

const NOT_PROBES = /^(facts-mixed|l4-review|l3-review)/;
/**
 * The skills a lesson's probe draws on: its own practice steps, not the warm-up or the spiral review — and never a Tigard
 * tool (Meliesa, Nov 4, 2026): a skill practiced on the keypad, the Map, Tigard Taps or a puzzle is probed with the
 * plain skill underneath it (plainProbes.ts), or left out when there is nothing to test without the tool.
 */
export function probeSkills(l: Lesson): string[] {
  const s: string[] = [];
  for (const st of l.steps) {
    if (st.kind !== "practice" || NOT_PROBES.test(st.skill) || st.id.startsWith("review") || isReviewSkill(st.skill)) continue;
    const id = st.skill in PROBE_SWAP ? PROBE_SWAP[st.skill] : st.skill;
    if (id && !s.includes(id)) s.push(id);
  }
  return s;
}
/** The lessons the diagnostic can probe, in teaching order (the cumulative reviews are left out). */
export const PROBE_LESSONS: Lesson[] = TEACHING.filter((l) => !/Review/.test(l.title) && probeSkills(l).length > 0);

export function probeItems(l: Lesson, rng: () => number): Item[] {
  const sk = probeSkills(l);
  const a = SKILLS[sk[0]!]!.gen(rng, 1);
  let b = SKILLS[(sk[1] ?? sk[0])!]!.gen(rng, 1);
  for (let k = 0; k < 10 && b.id === a.id; k++) b = SKILLS[(sk[1] ?? sk[0])!]!.gen(rng, 1);
  // A picture that does the comparing for the student is a scaffold; the diagnostic asks without it.
  const bare = (it: Item) => (/^frac-(compare|addsub)/.test(it.skill) ? { ...it, visual: undefined } : it);
  return [bare(a), bare(b)];
}
export function factItems(rng: () => number): Item[] {
  const tables = [9, 8, 7, 3, 4, 6, 12, 11, 9, 8, 7, 6];
  const mult = tables.slice(0, FACTS_N).map((t) => mixedFactItem(t, int(rng, 3, 12), false));
  const div = [9, 8, 7, 6, 4, 12].slice(0, DIV_N).map((t) => mixedFactItem(t, int(rng, 3, 12), true));
  return [...mult, ...div].map((it, i) => ({ ...it, id: `${it.id}:${i}` }));
}

/** Where to begin the lesson walk, from the student's grade (only a starting guess). */
export function startIndex(grade: number | null | undefined): number {
  const id = grade != null && grade <= 0 ? "LK.1" : grade === 1 ? "L1.1" : grade === 2 ? "L2.1" : grade == null || grade <= 3 ? "L3.1" : grade === 4 ? "L4.1" : grade === 5 ? "L5.1" : grade === 6 ? "L6.1" : grade === 7 ? "L7.1" : "L8.1";
  const i = PROBE_LESSONS.findIndex((l) => l.id === id);
  return i < 0 ? 0 : i;
}

export interface PlacementState {
  phase: "facts" | "up" | "down" | "fill" | "done";
  /** The highest lesson index shown so far, the lowest missed, and the current jump. */
  lo?: number;
  hi?: number;
  step?: number;
  facts: { right: number; n: number; ms: number[] };
  /** Index into PROBE_LESSONS being probed. */
  i: number;
  /** Lesson id → how many of its two probe problems were right. */
  probes: Record<string, number>;
  result?: string;
  solid?: boolean;
  divSolid?: boolean;
}
/** Where Tigard Times (the times tables) begins: a student not yet solid on the facts starts here. */
export const TIMES_START = "L3.1";
/** Second graders and younger haven't met the times tables yet: no facts sample, the lesson walk starts at Level 2. */
export const skipsFacts = (grade: number | null | undefined): boolean => grade != null && grade <= 2;
export const startState = (grade: number | null | undefined): PlacementState => ({ phase: skipsFacts(grade) ? "up" : "facts", facts: { right: 0, n: 0, ms: [] }, i: startIndex(grade), probes: {} });

const median = (xs: number[]) => { const s = [...xs].sort((a, b) => a - b); return s.length ? s[Math.floor(s.length / 2)]! : Infinity; };
export const factsSolid = (f: PlacementState["facts"]) => f.right >= FACTS_NEED && median(f.ms) <= FACTS_MEDIAN_MS;

/** Record the whole facts sample, then move on. */
export function afterFacts(s: PlacementState, results: { correct: boolean; ms: number }[]): PlacementState {
  const m = results.slice(0, FACTS_N), d = results.slice(FACTS_N);
  const facts = { right: m.filter((r) => r.correct).length, n: m.length, ms: m.filter((r) => r.correct).map((r) => r.ms) };
  const solid = factsSolid(facts);
  const dr = d.filter((r) => r.correct);
  const divSolid = solid && d.length > 0 && dr.length >= DIV_NEED && median(dr.map((r) => r.ms)) <= FACTS_MEDIAN_MS;
  if (!solid) return { ...s, facts, solid, divSolid, phase: "done", result: TIMES_START };
  return { ...s, facts, solid, divSolid, phase: "up" };
}
/**
 * Record one lesson's probe (how many of its two were right) and decide what comes next. A gallop, then a halving:
 * on passes the jump ahead doubles (2, 4, 8…); on misses the jump back doubles; once a shown lesson and a missed one
 * bracket the boundary, it checks halfway between until they are next to each other. The starting point is the first
 * lesson not shown. About twice log₂(lessons) probes at most.
 */
export function afterProbe(s: PlacementState, right: number): PlacementState {
  const l = PROBE_LESSONS[s.i]!;
  return settle({ ...s, probes: { ...s.probes, [l.id]: right } });
}
const N = () => PROBE_LESSONS.length;
const placeAt = (k: number) => (k >= N() ? TEACHING[TEACHING.length - 1]!.id : PROBE_LESSONS[Math.max(0, k)]!.id);
function settle(s: PlacementState): PlacementState {
  for (let guard = 0; guard < 200; guard++) {
    const id = PROBE_LESSONS[s.i]!.id;
    if (!(id in s.probes)) return s;
    const ok = s.probes[id] === 2;
    const lo = ok ? Math.max(s.lo ?? -1, s.i) : s.lo ?? -1, hi = ok ? s.hi ?? N() : Math.min(s.hi ?? N(), s.i);
    const step = s.step ?? STRIDE;
    if (s.phase === "up" && ok && hi === N()) {
      if (s.i === N() - 1) return { ...s, lo, hi, phase: "done", result: placeAt(N()) };
      s = { ...s, lo, hi, step: step * 2, i: Math.min(N() - 1, s.i + step) };
      continue;
    }
    if ((s.phase === "up" || s.phase === "down") && !ok && lo === -1) {
      if (s.i === 0) return { ...s, lo, hi, phase: "done", result: placeAt(0) };
      s = { ...s, phase: "down", lo, hi, step: step * 2, i: Math.max(0, s.i - step) };
      continue;
    }
    // bracketed: halve the gap
    if (hi - lo <= 1) return { ...s, lo, hi, phase: "done", result: placeAt(hi) };
    s = { ...s, phase: "fill", lo, hi, i: Math.floor((lo + hi) / 2) };
  }
  return s;
}

export const DIAG_LESSON: Lesson = {
  id: "diag", level: "all", num: 0, title: "Find My Starting Point", blurb: "the placement diagnostic",
  steps: [{ kind: "placement", id: "placement", title: "Find my starting point", intro: "A few problems to find where you begin. Do what you can — when something is new, say so and we'll move on. Nobody is expected to know all of it." }],
};
